﻿/* ============================================================
   StreetEdge Theme — Main JavaScript
   ============================================================ */

(function () {
  'use strict';

  /* ============================================================
     HAMBURGER / NAVIGATION
     ============================================================ */

  function initNav() {
    const hamburger  = document.getElementById('Hamburger');
    const navPanel   = document.getElementById('SiteNav');
    const navBackdrop = document.getElementById('NavBackdrop');
    const navClose   = document.getElementById('NavClose');
    const body       = document.body;

    if (!hamburger || !navPanel) return;

    function openNav() {
      body.classList.add('nav-open');
      navPanel.setAttribute('aria-hidden', 'false');
      hamburger.setAttribute('aria-expanded', 'true');
      navPanel.querySelector('a, button')?.focus();
      trapFocus(navPanel);
    }

    function closeNav() {
      body.classList.remove('nav-open');
      navPanel.setAttribute('aria-hidden', 'true');
      hamburger.setAttribute('aria-expanded', 'false');
      releaseFocus();
      hamburger.focus();
    }

    hamburger.addEventListener('click', () => {
      body.classList.contains('nav-open') ? closeNav() : openNav();
    });

    navClose?.addEventListener('click', closeNav);
    navBackdrop?.addEventListener('click', closeNav);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && body.classList.contains('nav-open')) closeNav();
    });

    // Submenu toggles
    navPanel.querySelectorAll('[data-submenu-toggle]').forEach(toggle => {
      toggle.addEventListener('click', function (e) {
        e.preventDefault();
        const targetId = this.dataset.submenuToggle;
        const submenu  = document.getElementById(targetId);
        if (!submenu) return;
        const isOpen = submenu.classList.toggle('is-open');
        this.setAttribute('aria-expanded', String(isOpen));
      });
    });
  }

  /* ============================================================
     CART DRAWER
     ============================================================ */

  function initCartDrawer() {
    const cartType = document.body.dataset.cartType;
    if (cartType !== 'drawer') return;

    const drawer    = document.getElementById('CartDrawer');
    const overlay   = document.getElementById('CartDrawerOverlay');
    const closeBtn  = document.getElementById('CartDrawerClose');
    const body      = document.getElementById('CartDrawerBody');
    const footer    = document.getElementById('CartDrawerFooter');
    const countEls  = document.querySelectorAll('[data-cart-count]');

    if (!drawer) return;

    function openDrawer() {
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      fetchCart();
    }

    function closeDrawer() {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
    }

    overlay?.addEventListener('click', closeDrawer);
    closeBtn?.addEventListener('click', closeDrawer);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) closeDrawer();
    });

    // Open cart on icon click
    document.querySelectorAll('[data-open-cart]').forEach(el => {
      el.addEventListener('click', (e) => { e.preventDefault(); openDrawer(); });
    });

    async function fetchCart() {
      body.innerHTML = '<p class="cart-drawer__empty">Loading...</p>';
      try {
        const res  = await fetch('/cart.js');
        const cart = await res.json();
        renderCart(cart);
        updateCartCount(cart.item_count);
      } catch (err) {
        body.innerHTML = '<p class="cart-drawer__empty">Could not load cart.</p>';
      }
    }

    function renderCart(cart) {
      if (cart.item_count === 0) {
        body.innerHTML = `
          <p class="cart-drawer__empty">Your cart is empty.</p>
          <div style="text-align:center;margin-top:1rem;">
            <a href="/collections/all" class="btn btn--outline">Continue Shopping</a>
          </div>`;
        footer.innerHTML = '';
        return;
      }

      const itemsHtml = cart.items.map(item => `
        <div class="cart-item" data-key="${item.key}">
          <a href="${item.url}">
            <img class="cart-item__image" src="${item.image}" alt="${item.product_title}" width="70" height="70">
          </a>
          <div class="cart-item__info">
            <p class="cart-item__vendor">${item.vendor}</p>
            <a href="${item.url}" class="cart-item__name">${item.product_title}</a>
            <p class="cart-item__variant">${item.variant_title !== 'Default Title' ? item.variant_title : ''}</p>
            <div class="cart-item__bottom">
              <span class="cart-item__price">${formatMoney(item.final_line_price)}</span>
              <button class="cart-item__remove" data-remove="${item.key}">Remove</button>
            </div>
          </div>
        </div>
      `).join('');

      body.innerHTML = itemsHtml;

      footer.innerHTML = `
        <div class="cart-subtotal">
          <span class="cart-subtotal__label">Subtotal</span>
          <span class="cart-subtotal__amount">${formatMoney(cart.total_price)}</span>
        </div>
        <a href="/checkout" class="btn btn--primary btn--full">Checkout</a>
        <a href="/cart" class="btn btn--outline btn--full" style="margin-top:0.5rem;">View Cart</a>
      `;

      // Remove item handlers
      body.querySelectorAll('[data-remove]').forEach(btn => {
        btn.addEventListener('click', async () => {
          const key = btn.dataset.remove;
          await updateCartItem(key, 0);
          fetchCart();
        });
      });
    }

    function updateCartCount(count) {
      countEls.forEach(el => {
        el.textContent  = count;
        el.dataset.count = count;
      });
    }

    async function updateCartItem(key, quantity) {
      await fetch('/cart/change.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ id: key, quantity }),
      });
    }

    window.StreetEdge = window.StreetEdge || {};
    window.StreetEdge.openCart   = openDrawer;
    window.StreetEdge.refreshCart = fetchCart;
    window.StreetEdge.updateCartCount = updateCartCount;
  }

  /* ============================================================
     ADD TO CART
     ============================================================ */

  function initAddToCart() {
    document.querySelectorAll('[data-product-form]').forEach(form => {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();

        const submitBtn = form.querySelector('[data-atc-btn]');
        const formData  = new FormData(form);

        submitBtn?.classList.add('btn--loading');
        submitBtn && (submitBtn.disabled = true);

        try {
          const res  = await fetch('/cart/add.js', { method: 'POST', body: formData });
          const data = await res.json();

          if (data.status) {
            showToast(data.description || 'Could not add to cart', 'error');
          } else {
            showToast('Added to cart!', 'success');
            const cartType = document.body.dataset.cartType;
            if (cartType === 'drawer') {
              window.StreetEdge?.openCart();
              window.StreetEdge?.refreshCart();
            }
            // Update cart count
            const cartRes  = await fetch('/cart.js');
            const cart     = await cartRes.json();
            window.StreetEdge?.updateCartCount(cart.item_count);
          }
        } catch (err) {
          showToast('Something went wrong. Please try again.', 'error');
        } finally {
          submitBtn?.classList.remove('btn--loading');
          if (submitBtn) submitBtn.disabled = false;
        }
      });
    });

    // Quick-add buttons on collection cards
    document.querySelectorAll('[data-quick-add]').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const variantId = btn.dataset.quickAdd;
        if (!variantId) return;

        btn.classList.add('btn--loading');
        btn.disabled = true;

        try {
          const res = await fetch('/cart/add.js', {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify({ id: variantId, quantity: 1 }),
          });
          const data = await res.json();

          if (data.status) {
            showToast(data.description || 'Could not add to cart', 'error');
          } else {
            showToast('Added to cart!', 'success');
            if (document.body.dataset.cartType === 'drawer') {
              window.StreetEdge?.openCart();
              window.StreetEdge?.refreshCart();
            }
            const cartRes = await fetch('/cart.js');
            const cart    = await cartRes.json();
            window.StreetEdge?.updateCartCount(cart.item_count);
          }
        } catch {
          showToast('Something went wrong.', 'error');
        } finally {
          btn.classList.remove('btn--loading');
          btn.disabled = false;
        }
      });
    });
  }

  /* ============================================================
     VARIANT PICKER (Product Page)
     ============================================================ */

  function initVariantPicker() {
    document.querySelectorAll('[data-variant-picker]').forEach(picker => {
      const form         = picker.closest('[data-product-form]') || document.querySelector('[data-product-form]');
      const productData  = JSON.parse(document.getElementById('ProductData')?.textContent || '{}');
      const variants     = productData.variants || [];
      const options      = productData.options  || [];

      if (!variants.length) return;

      let selectedOptions = options.map((_, i) => {
        return picker.querySelector(`[data-option-index="${i}"].is-selected`)?.dataset.value
            || variants[0][`option${i + 1}`];
      });

      function findVariant() {
        return variants.find(v =>
          v.option1 === selectedOptions[0] &&
          v.option2 === (selectedOptions[1] ?? v.option2) &&
          v.option3 === (selectedOptions[2] ?? v.option3)
        ) || null;
      }

      function updateAvailability() {
        picker.querySelectorAll('[data-option-btn]').forEach(btn => {
          const optIdx = parseInt(btn.dataset.optionIndex);
          const val    = btn.dataset.value;
          const testOpts = [...selectedOptions];
          testOpts[optIdx] = val;

          const match = variants.find(v =>
            v.option1 === testOpts[0] &&
            v.option2 === (testOpts[1] ?? v.option2) &&
            v.option3 === (testOpts[2] ?? v.option3)
          );

          btn.classList.toggle('is-unavailable', !match || !match.available);
        });
      }

      function updateVariant() {
        const variant = findVariant();
        if (!variant) return;

        // Update hidden input
        const input = form?.querySelector('[name="id"]');
        if (input) input.value = variant.id;

        // Update price display
        const priceEl = document.querySelector('[data-product-price]');
        const compareEl = document.querySelector('[data-product-compare-price]');
        if (priceEl)    priceEl.textContent    = formatMoney(variant.price);
        if (compareEl) {
          if (variant.compare_at_price > variant.price) {
            compareEl.textContent = formatMoney(variant.compare_at_price);
            compareEl.style.display = 'inline';
          } else {
            compareEl.style.display = 'none';
          }
        }

        // Update add-to-cart button
        const atcBtn = form?.querySelector('[data-atc-btn]');
        if (atcBtn) {
          if (!variant.available) {
            atcBtn.textContent = 'Sold Out';
            atcBtn.disabled    = true;
          } else {
            atcBtn.textContent = 'Add to Cart';
            atcBtn.disabled    = false;
          }
        }

        // Update gallery to show variant image
        if (variant.featured_image) {
          const thumbs = document.querySelectorAll('[data-gallery-thumb]');
          thumbs.forEach(thumb => {
            if (thumb.src.includes(variant.featured_image.src?.split('?')[0]?.split('/').pop()?.split('.')[0])) {
              thumb.click();
            }
          });
        }

        // Update URL without reload
        const url = new URL(window.location);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);

        updateAvailability();
      }

      // Attach click handlers
      picker.querySelectorAll('[data-option-btn]').forEach(btn => {
        btn.addEventListener('click', function () {
          const optIdx = parseInt(this.dataset.optionIndex);
          const val    = this.dataset.value;

          // Deselect siblings
          picker.querySelectorAll(`[data-option-index="${optIdx}"]`).forEach(b => b.classList.remove('is-selected'));
          this.classList.add('is-selected');

          // Update label
          const labelEl = picker.querySelector(`[data-option-selected="${optIdx}"]`);
          if (labelEl) labelEl.textContent = val;

          selectedOptions[optIdx] = val;
          updateVariant();
        });
      });

      updateAvailability();
    });
  }

  /* ============================================================
     PRODUCT GALLERY
     ============================================================ */

  function initProductGallery() {
    const mainImg = document.getElementById('GalleryMainImg');
    if (!mainImg) return;

    document.querySelectorAll('[data-gallery-thumb]').forEach(thumb => {
      thumb.addEventListener('click', function () {
        const src = this.dataset.src;
        const alt = this.dataset.alt || '';

        mainImg.src = src;
        mainImg.alt = alt;

        document.querySelectorAll('[data-gallery-thumb]').forEach(t => t.classList.remove('is-active'));
        this.classList.add('is-active');
      });
    });
  }

  /* ============================================================
     SIZE CHART MODAL
     ============================================================ */

  function initSizeChart() {
    const modal    = document.getElementById('SizeChartModal');
    const overlay  = document.getElementById('SizeChartOverlay');
    const closeBtn = document.getElementById('SizeChartClose');

    if (!modal) return;

    const modalImg = modal.querySelector('.modal__body img');

    function open(imgSrc) {
      if (imgSrc && modalImg) {
        modalImg.src = imgSrc;
        modalImg.style.display = 'block';
      }
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      closeBtn?.focus();
      trapFocus(modal);
    }

    function close() {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      releaseFocus();
    }

    document.querySelectorAll('[data-open-size-chart]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        open(btn.dataset.sizeChartImg);
      });
    });

    overlay?.addEventListener('click', close);
    closeBtn?.addEventListener('click', close);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) close();
    });
  }

  /* ============================================================
     COLLECTION FILTERS
     ============================================================ */

  function initCollectionFilters() {
    const configEl = document.getElementById('FilterConfig');
    if (!configEl) return;

    let config;
    try { config = JSON.parse(configEl.textContent); } catch { return; }

    const sidebar        = document.getElementById('FilterSidebar');
    const backdrop       = document.getElementById('FilterBackdrop');
    const filterBody     = document.getElementById('FilterBody');
    const filterBadge    = document.getElementById('FilterBadge');
    const filteredCount  = document.getElementById('FilteredCount');
    const applyCount     = document.getElementById('FilterApplyCount');
    const activeFiltersEl = document.getElementById('ActiveFilters');
    const noResultsEl    = document.getElementById('FilterNoResults');

    let filterState = {};   // { key: Set<string> }
    let filterOptions = {}; // { key: [{label, value}] }
    let priceState = null;    // null = inactive; {min,max} in cents when active
    let priceAbsolute = null; // {min,max} in cents from product data
    let allProductsData = null; // full-collection dataset objects for availability testing

    /* ---- sidebar open/close ---- */
    function openSidebar() {
      sidebar.classList.add('is-open');
      sidebar.setAttribute('aria-hidden', 'false');
      backdrop?.classList.add('is-visible');
      document.body.style.overflow = 'hidden';
    }
    function closeSidebar() {
      sidebar.classList.remove('is-open');
      sidebar.setAttribute('aria-hidden', 'true');
      backdrop?.classList.remove('is-visible');
      document.body.style.overflow = '';
    }

    document.querySelectorAll('[data-open-filters]').forEach(b => b.addEventListener('click', openSidebar));
    document.querySelectorAll('[data-close-filters]').forEach(b => b.addEventListener('click', closeSidebar));
    backdrop?.addEventListener('click', closeSidebar);
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && sidebar?.classList.contains('is-open')) closeSidebar();
    });

    /* ---- fetch all products (up to 250 per page, all pages) ---- */
    async function fetchProducts(handle) {
      const products = [];
      let page = 1;
      while (true) {
        const res = await fetch(`/collections/${handle}/products.json?limit=250&page=${page}`);
        if (!res.ok) break;
        const data = await res.json();
        if (!data.products?.length) break;
        products.push(...data.products);
        if (data.products.length < 250) break;
        page++;
      }
      return products;
    }

    /* ---- extract filter values from API products ---- */
    function extractSizes(products) {
      const sizeOrder = ['XXS','XS','S','M','L','XL','XXL','XXXL','XXXXL'];
      const found = new Map(); // uppercase key → display label
      products.forEach(p => {
        const idx = p.options.findIndex(o => o.name.toLowerCase() === 'size');
        if (idx === -1) return;
        const key = `option${idx + 1}`;
        p.variants.forEach(v => { if (v.available) found.set(v[key].toUpperCase(), v[key]); });
      });
      const sorted = [];
      sizeOrder.forEach(s => { if (found.has(s)) sorted.push({ label: found.get(s), value: s.toLowerCase() }); });
      found.forEach((lbl, key) => { if (!sizeOrder.includes(key)) sorted.push({ label: lbl, value: key.toLowerCase() }); });
      return sorted;
    }

    function extractBrands(products) {
      const s = new Set();
      products.forEach(p => { if (p.vendor) s.add(p.vendor); });
      return [...s].sort().map(b => ({ label: b, value: b.toLowerCase() }));
    }

    function extractTypes(products) {
      const s = new Set();
      products.forEach(p => { if (p.product_type) s.add(p.product_type); });
      return [...s].sort().map(t => ({ label: t, value: t.toLowerCase() }));
    }

    function extractColours(products, colourTags) {
      const norm = colourTags.map(c => c.trim().toLowerCase()).filter(Boolean);
      const found = new Map();
      products.forEach(p => {
        p.tags.forEach(tag => {
          const lo = tag.trim().toLowerCase();
          if (norm.includes(lo) && !found.has(lo))
            found.set(lo, lo.charAt(0).toUpperCase() + lo.slice(1));
        });
      });
      return [...found.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([v, l]) => ({ label: l, value: v }));
    }

    function buildFilterOptions(products) {
      const opts = {};
      if (config.filters.availability) opts.availability = [{ label: 'In stock only', value: 'true' }];
      if (config.filters.size) { const r = extractSizes(products); if (r.length) opts.size = r; }
      if (config.filters.brand) { const r = extractBrands(products); if (r.length > 1) opts.brand = r; }
      if (config.filters.type) { const r = extractTypes(products); if (r.length > 1) opts.type = r; }
      if (config.filters.colour) { const r = extractColours(products, config.filters.colourTags); if (r.length) opts.colour = r; }
      config.customFilters.forEach(cf => {
        const tags = cf.tags.map(t => t.trim().toLowerCase()).filter(Boolean);
        if (!tags.length) return;
        const allTags = products.flatMap(p => p.tags.map(t => t.toLowerCase()));
        const avail = tags.filter(t => allTags.includes(t));
        if (avail.length) opts['custom__' + cf.label] = avail.map(t => ({ label: t.charAt(0).toUpperCase() + t.slice(1), value: t }));
      });
      return opts;
    }

    function buildDatasetsFromProducts(products) {
      return products.map(p => {
        const sizeIdx = p.options.findIndex(o => o.name.toLowerCase() === 'size');
        let sizes = '';
        if (sizeIdx !== -1) {
          const key = `option${sizeIdx + 1}`;
          p.variants.forEach(v => { if (v.available) sizes += v[key].toLowerCase() + '|'; });
        }
        let priceMin = Infinity;
        p.variants.forEach(v => {
          const c = Math.round(parseFloat(v.price || '0') * 100);
          if (c < priceMin) priceMin = c;
        });
        return {
          dataset: {
            available: String(p.variants.some(v => v.available)),
            vendor: (p.vendor || '').toLowerCase(),
            type: (p.product_type || '').toLowerCase(),
            sizes: sizes,
            price: String(priceMin === Infinity ? 0 : priceMin),
            tags: p.tags.map(t => t.toLowerCase()).join('|')
          }
        };
      });
    }

    /* ---- price range helpers ---- */

    function extractPriceRange(products) {
      let min = Infinity, max = -Infinity;
      products.forEach(p => {
        p.variants.forEach(v => {
          // products.json API returns prices as decimal strings e.g. "29.99"
          const price = Math.round(parseFloat(v.price || '0') * 100);
          if (price < min) min = price;
          if (price > max) max = price;
        });
      });
      if (min === Infinity) return { min: 0, max: 10000 };
      return { min: Math.floor(min / 100) * 100, max: Math.ceil(max / 100) * 100 };
    }

    function priceSliderHTML(absMin, absMax) {
      const range = absMax - absMin;
      const step = range <= 5000 ? 100 : range <= 20000 ? 500 : 1000;
      return `
        <div class="filter-group" id="PriceFilterGroup">
          <button class="filter-group__toggle is-open" type="button" aria-expanded="true" id="PriceGroupToggle">
            <span>Price</span>
            <svg class="filter-group__chevron" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path d="M2.5 5L7 9.5L11.5 5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <div class="filter-group__body" id="PriceGroupBody">
            <div class="filter-price">
              <div class="filter-price__slider">
                <input type="range" class="filter-price__thumb filter-price__thumb--min"
                  id="PriceRangeMin" min="${absMin}" max="${absMax}" value="${absMin}" step="${step}"
                  aria-label="Minimum price">
                <input type="range" class="filter-price__thumb filter-price__thumb--max"
                  id="PriceRangeMax" min="${absMin}" max="${absMax}" value="${absMax}" step="${step}"
                  aria-label="Maximum price">
                <div class="filter-price__track">
                  <div class="filter-price__fill" id="PriceRangeFill"></div>
                </div>
              </div>
              <div class="filter-price__labels">
                <span class="filter-price__label" id="PriceRangeMinLabel">${formatMoney(absMin)}</span>
                <span class="filter-price__label" id="PriceRangeMaxLabel">${formatMoney(absMax)}</span>
              </div>
            </div>
          </div>
        </div>`;
    }

    function initPriceSlider(absMin, absMax) {
      const minInput  = document.getElementById('PriceRangeMin');
      const maxInput  = document.getElementById('PriceRangeMax');
      const fill      = document.getElementById('PriceRangeFill');
      const minLabel  = document.getElementById('PriceRangeMinLabel');
      const maxLabel  = document.getElementById('PriceRangeMaxLabel');
      const toggle    = document.getElementById('PriceGroupToggle');
      const groupBody = document.getElementById('PriceGroupBody');
      if (!minInput || !maxInput || !fill) return;

      const step = parseInt(minInput.step) || 100;

      function updateFill() {
        const lo  = parseInt(minInput.value);
        const hi  = parseInt(maxInput.value);
        const pct = (v) => ((v - absMin) / (absMax - absMin)) * 100;
        fill.style.left  = pct(lo) + '%';
        fill.style.width = (pct(hi) - pct(lo)) + '%';
        if (minLabel) minLabel.textContent = formatMoney(lo);
        if (maxLabel) maxLabel.textContent = formatMoney(hi);
      }

      updateFill();

      minInput.addEventListener('input', () => {
        if (parseInt(minInput.value) > parseInt(maxInput.value) - step)
          minInput.value = String(parseInt(maxInput.value) - step);
        updateFill();
      });

      maxInput.addEventListener('input', () => {
        if (parseInt(maxInput.value) < parseInt(minInput.value) + step)
          maxInput.value = String(parseInt(minInput.value) + step);
        updateFill();
      });

      function commitPrice() {
        const lo = parseInt(minInput.value);
        const hi = parseInt(maxInput.value);
        priceState = (lo === absMin && hi === absMax) ? null : { min: lo, max: hi };
        applyFilters();
        renderChips();
      }

      minInput.addEventListener('change', commitPrice);
      maxInput.addEventListener('change', commitPrice);

      toggle?.addEventListener('click', () => {
        const open = toggle.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', String(open));
        if (groupBody) groupBody.hidden = !open;
      });
    }

    /* ---- render filter panel ---- */
    const groupLabels = { availability: 'In Stock', size: 'Size', brand: 'Brand', type: 'Product Type', colour: 'Colour' };

    function renderPanel(opts) {
      if (!filterBody) return;
      let html = '';
      Object.entries(opts).forEach(([key, options]) => {
        const label = groupLabels[key] || key.replace('custom__', '');
        html += `
          <div class="filter-group">
            <button class="filter-group__toggle is-open" type="button" aria-expanded="true">
              <span>${label}</span>
              <svg class="filter-group__chevron" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <path d="M2.5 5L7 9.5L11.5 5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
            <div class="filter-group__body">
              ${options.map(opt => `
                <label class="filter-option">
                  <input class="filter-cb" type="checkbox" data-key="${key}" data-value="${escapeHTML(opt.value)}">
                  <span class="filter-option__box"></span>
                  <span class="filter-option__label">${escapeHTML(opt.label)}</span>
                </label>
              `).join('')}
            </div>
          </div>`;
      });
      if (priceAbsolute) html = priceSliderHTML(priceAbsolute.min, priceAbsolute.max) + html;
      filterBody.innerHTML = html || '<p class="filter-loading">No filters available for this collection.</p>';
      if (priceAbsolute) initPriceSlider(priceAbsolute.min, priceAbsolute.max);

      filterBody.querySelectorAll('.filter-group__toggle').forEach(toggle => {
        toggle.addEventListener('click', () => {
          const open = toggle.classList.toggle('is-open');
          toggle.setAttribute('aria-expanded', open);
          toggle.nextElementSibling.hidden = !open;
        });
      });

      filterBody.querySelectorAll('.filter-cb').forEach(cb => {
        cb.addEventListener('change', () => {
          const { key, value } = cb.dataset;
          if (!filterState[key]) filterState[key] = new Set();
          cb.checked ? filterState[key].add(value) : filterState[key].delete(value);
          applyFilters();
          renderChips();
        });
      });

      restoreCheckboxes();
    }

    function restoreCheckboxes() {
      Object.entries(filterState).forEach(([key, vals]) => {
        vals.forEach(val => {
          const cb = filterBody?.querySelector(`.filter-cb[data-key="${key}"][data-value="${val}"]`);
          if (cb) cb.checked = true;
        });
      });
    }

    /* ---- apply filters to rendered cards ---- */
    function applyFilters() {
      const cards = document.querySelectorAll('.product-card');
      let visible = 0;

      cards.forEach(card => {
        const show = matches(card);
        card.style.display = show ? '' : 'none';
        if (show) visible++;
      });

      if (filteredCount) filteredCount.textContent = visible;
      if (applyCount) applyCount.textContent = visible;
      if (noResultsEl) noResultsEl.hidden = visible > 0;

      updateBadge();
      updateOptionAvailability();
    }

    function matches(card) {
      // Price range check (data-price is in cents from Liquid)
      if (priceState) {
        const price = parseInt(card.dataset.price || '0');
        if (price < priceState.min || price > priceState.max) return false;
      }
      for (const [key, vals] of Object.entries(filterState)) {
        if (!vals.size) continue;
        if (key === 'availability') {
          if (vals.has('true') && card.dataset.available !== 'true') return false;
        } else if (key === 'size') {
          const sizes = (card.dataset.sizes || '').split('|').filter(Boolean);
          if (![...vals].some(v => sizes.includes(v))) return false;
        } else if (key === 'brand') {
          if (!vals.has(card.dataset.vendor || '')) return false;
        } else if (key === 'type') {
          if (!vals.has(card.dataset.type || '')) return false;
        } else {
          // colour + custom tag filters
          const tags = (card.dataset.tags || '').split('|').filter(Boolean);
          if (![...vals].some(v => tags.includes(v))) return false;
        }
      }
      return true;
    }

    function matchesWithState(card, testFilterState) {
      if (priceState) {
        const price = parseInt(card.dataset.price || '0');
        if (price < priceState.min || price > priceState.max) return false;
      }
      for (const [key, vals] of Object.entries(testFilterState)) {
        if (!vals.size) continue;
        if (key === 'availability') {
          if (vals.has('true') && card.dataset.available !== 'true') return false;
        } else if (key === 'size') {
          const sizes = (card.dataset.sizes || '').split('|').filter(Boolean);
          if (![...vals].some(v => sizes.includes(v))) return false;
        } else if (key === 'brand') {
          if (!vals.has(card.dataset.vendor || '')) return false;
        } else if (key === 'type') {
          if (!vals.has(card.dataset.type || '')) return false;
        } else {
          const tags = (card.dataset.tags || '').split('|').filter(Boolean);
          if (![...vals].some(v => tags.includes(v))) return false;
        }
      }
      return true;
    }

    function updateOptionAvailability() {
      if (!filterBody || !allProductsData) return;
      const allCards = allProductsData;
      filterBody.querySelectorAll('.filter-cb').forEach(cb => {
        const key = cb.dataset.key;
        const val = cb.dataset.value;
        if (cb.checked) {
          cb.disabled = false;
          cb.closest('.filter-option')?.classList.remove('is-unavailable');
          return;
        }
        const testState = {};
        for (const [k, v] of Object.entries(filterState)) {
          testState[k] = new Set(v);
        }
        if (!testState[key]) testState[key] = new Set();
        testState[key].add(val);
        let hasMatch = false;
        for (const card of allCards) {
          if (matchesWithState(card, testState)) { hasMatch = true; break; }
        }
        cb.disabled = !hasMatch;
        cb.closest('.filter-option')?.classList.toggle('is-unavailable', !hasMatch);
      });
    }

    /* ---- active filter chips ---- */
    function renderChips() {
      if (!activeFiltersEl) return;
      const chips = [];

      Object.entries(filterState).forEach(([key, vals]) => {
        const groupLabel = groupLabels[key] || key.replace('custom__', '');
        vals.forEach(val => {
          const opt = (filterOptions[key] || []).find(o => o.value === val);
          chips.push(`
            <button class="active-filter-chip" data-remove-key="${key}" data-remove-val="${escapeHTML(val)}" type="button">
              ${escapeHTML(groupLabel)}: ${escapeHTML(opt?.label || val)}
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                <path d="M1 1L9 9M9 1L1 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              </svg>
            </button>`);
        });
      });

      if (priceState) {
        chips.push(`
          <button class="active-filter-chip" data-remove-price type="button">
            Price: ${formatMoney(priceState.min)}&ndash;${formatMoney(priceState.max)}
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true"><path d="M1 1L9 9M9 1L1 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
          </button>`);
      }

      activeFiltersEl.hidden = chips.length === 0;
      if (!chips.length) { activeFiltersEl.innerHTML = ''; return; }

      activeFiltersEl.innerHTML = chips.join('') +
        `<button class="active-filter-chip active-filter-chip--clear" id="ChipClearAll" type="button">Clear all</button>`;

      activeFiltersEl.querySelectorAll('[data-remove-key]').forEach(btn => {
        btn.addEventListener('click', () => {
          const { removeKey: key, removeVal: val } = btn.dataset;
          filterState[key]?.delete(val);
          const cb = filterBody?.querySelector(`.filter-cb[data-key="${key}"][data-value="${val}"]`);
          if (cb) cb.checked = false;
          applyFilters(); renderChips();
        });
      });

      activeFiltersEl.querySelectorAll('[data-remove-price]').forEach(btn => {
        btn.addEventListener('click', () => {
          priceState = null;
          const mi = document.getElementById('PriceRangeMin');
          const ma = document.getElementById('PriceRangeMax');
          if (mi) { mi.value = mi.min; mi.dispatchEvent(new Event('input')); }
          if (ma) { ma.value = ma.max; ma.dispatchEvent(new Event('input')); }
          applyFilters(); renderChips();
        });
      });

      activeFiltersEl.querySelector('#ChipClearAll')?.addEventListener('click', clearAll);
    }

    function clearAll() {
      filterState = {};
      priceState = null;
      filterBody?.querySelectorAll('.filter-cb').forEach(cb => cb.checked = false);
      const mi = document.getElementById('PriceRangeMin');
      const ma = document.getElementById('PriceRangeMax');
      if (mi) { mi.value = mi.min; mi.dispatchEvent(new Event('input')); }
      if (ma) { ma.value = ma.max; ma.dispatchEvent(new Event('input')); }
      applyFilters(); renderChips();
    }

    function updateBadge() {
      if (!filterBadge) return;
      let total = 0;
      Object.values(filterState).forEach(s => total += s.size);
      if (priceState) total++;
      filterBadge.hidden = total === 0;
      filterBadge.textContent = total;
    }

    document.getElementById('FilterClearAll')?.addEventListener('click', clearAll);
    document.getElementById('FilterClearNoResults')?.addEventListener('click', clearAll);

    /* expose for infinite scroll hook */
    window.StreetEdge = window.StreetEdge || {};
    window.StreetEdge.reapplyFilters = applyFilters;

    /* ---- build pseudo-products from rendered card DOM (search mode) ---- */
    function buildProductsFromDOM() {
      return [...document.querySelectorAll('.product-card')].map(card => {
        const sizesRaw = (card.dataset.sizes || '').split('|').filter(Boolean);
        const vendor = card.dataset.vendor || '';
        const vendorDisplay = vendor.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
        const typeRaw = card.dataset.type || '';
        const typeDisplay = typeRaw.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
        return {
          vendor: vendorDisplay,
          product_type: typeDisplay,
          tags: (card.dataset.tags || '').split('|').filter(Boolean),
          options: sizesRaw.length ? [{ name: 'Size' }] : [],
          variants: sizesRaw.map(s => ({ available: true, option1: s }))
        };
      });
    }

    /* ---- init: fetch products or read DOM, build panel ---- */
    if (config.searchMode) {
      const domProducts = buildProductsFromDOM();
      filterOptions = buildFilterOptions(domProducts);
      allProductsData = [...document.querySelectorAll('.product-card')];
      renderPanel(filterOptions);
      const total = document.querySelectorAll('.product-card').length;
      if (filteredCount) filteredCount.textContent = total;
      if (applyCount) applyCount.textContent = total;
    } else {
      fetchProducts(config.collectionHandle).then(products => {
        priceAbsolute = extractPriceRange(products);
        filterOptions = buildFilterOptions(products);
        allProductsData = buildDatasetsFromProducts(products);
        renderPanel(filterOptions);
        if (filteredCount) filteredCount.textContent = config.totalProducts;
        if (applyCount) applyCount.textContent = config.totalProducts;
      }).catch(() => {
        if (filterBody) filterBody.innerHTML = '<p class="filter-loading">Filters unavailable.</p>';
      });
    }
  }

  /* ============================================================
     INFINITE SCROLL (Collection Pages)
     ============================================================ */

  function initInfiniteScroll() {
    const grid       = document.querySelector('[data-product-grid]');
    const sentinel   = document.getElementById('InfiniteScrollSentinel');
    const toggle     = document.getElementById('InfiniteScrollToggle');
    const loader     = document.getElementById('InfiniteLoader');
    const pagination = document.getElementById('PaginationNav');

    if (!grid || !sentinel) return;

    let nextUrl     = sentinel.dataset.nextUrl || null;
    let loading     = false;
    let isEnabled   = toggle ? toggle.classList.contains('is-on') : true;
    let observer;

    function setEnabled(val) {
      isEnabled = val;
      if (val) {
        observer?.observe(sentinel);
      } else {
        observer?.unobserve(sentinel);
        if (loader) loader.style.display = 'none';
      }
      if (pagination) pagination.style.display = val ? 'none' : '';
    }

    toggle?.addEventListener('click', () => {
      toggle.classList.toggle('is-on');
      setEnabled(toggle.classList.contains('is-on'));
      // Persist choice
      try { localStorage.setItem('se_infinite_scroll', isEnabled ? '1' : '0'); } catch {}
    });

    // Restore preference
    try {
      const saved = localStorage.getItem('se_infinite_scroll');
      if (saved !== null) {
        const on = saved === '1';
        if (on !== isEnabled) {
          toggle?.classList.toggle('is-on', on);
          isEnabled = on;
        }
      }
    } catch {}

    async function loadMore() {
      if (loading || !nextUrl || !isEnabled) return;

      loading = true;
      if (loader) loader.style.display = 'flex';

      try {
        const res  = await fetch(nextUrl);
        const html = await res.text();
        const doc  = new DOMParser().parseFromString(html, 'text/html');

        const newGrid = doc.querySelector('[data-product-grid]');
        const newSentinel = doc.getElementById('InfiniteScrollSentinel');

        if (newGrid) {
          const newCards = newGrid.querySelectorAll('.product-card');
          newCards.forEach(card => {
            // Reset animation delay for new items
            card.style.animationDelay = '0s';
            grid.appendChild(card.cloneNode(true));
          });

          // Re-init quick-add on newly added cards
          initQuickAddOnCards(grid);
          // Re-apply active filters to newly loaded cards
          window.StreetEdge?.reapplyFilters?.();
        }

        nextUrl = newSentinel?.dataset.nextUrl || null;
        sentinel.dataset.nextUrl = nextUrl || '';

        if (!nextUrl) {
          observer?.disconnect();
          if (loader) loader.style.display = 'none';
        }
      } catch (err) {
        console.error('Infinite scroll error:', err);
      } finally {
        loading = false;
        if (loader && nextUrl) loader.style.display = 'none';
      }
    }

    observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) loadMore();
    }, { rootMargin: '400px' });

    if (isEnabled && nextUrl) {
      observer.observe(sentinel);
    } else if (!nextUrl && loader) {
      loader.style.display = 'none';
    }

    // Set initial pagination visibility to match scroll state
    if (pagination) pagination.hidden = isEnabled;
  }

  function initQuickAddOnCards(container) {
    container.querySelectorAll('[data-quick-add]:not([data-bound])').forEach(btn => {
      btn.dataset.bound = '1';
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const variantId = btn.dataset.quickAdd;
        if (!variantId) return;
        btn.classList.add('btn--loading');
        btn.disabled = true;
        try {
          const res = await fetch('/cart/add.js', {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify({ id: variantId, quantity: 1 }),
          });
          const data = await res.json();
          if (data.status) {
            showToast(data.description || 'Error', 'error');
          } else {
            showToast('Added!', 'success');
            if (document.body.dataset.cartType === 'drawer') {
              window.StreetEdge?.openCart();
              window.StreetEdge?.refreshCart();
            }
            const cartRes = await fetch('/cart.js');
            const cart = await cartRes.json();
            window.StreetEdge?.updateCartCount(cart.item_count);
          }
        } catch { showToast('Error', 'error'); }
        finally {
          btn.classList.remove('btn--loading');
          btn.disabled = false;
        }
      });
    });
  }

  /* ============================================================
     INSTAGRAM FEED
     ============================================================ */

  function initInstagramFeed() {
    const container = document.getElementById('InstagramFeedContainer');
    if (!container) return;

    const token = container.dataset.token;
    const count = parseInt(container.dataset.count || '9');

    if (!token) return; // Manual images shown in Liquid

    const url = `https://graph.instagram.com/me/media?fields=id,media_type,media_url,thumbnail_url,permalink,like_count,comments_count&limit=${count}&access_token=${token}`;

    fetch(url)
      .then(r => r.json())
      .then(data => {
        if (!data.data || data.error) {
          container.innerHTML = `<p class="instagram-placeholder">Could not load Instagram feed. Please check your access token in Theme Settings.</p>`;
          return;
        }

        const html = data.data
          .filter(post => post.media_type !== 'VIDEO' || post.thumbnail_url)
          .map(post => {
            const imgSrc = post.media_type === 'VIDEO' ? post.thumbnail_url : post.media_url;
            return `
              <a href="${post.permalink}" class="instagram-item" target="_blank" rel="noopener" aria-label="View on Instagram">
                <img class="instagram-item__img" src="${imgSrc}" alt="Instagram post" loading="lazy">
                <div class="instagram-item__overlay">
                  ${post.like_count ? `<span class="instagram-item__likes">♥ ${post.like_count}</span>` : ''}
                  ${post.comments_count ? `<span class="instagram-item__comments">💬 ${post.comments_count}</span>` : ''}
                </div>
              </a>`;
          }).join('');

        container.innerHTML = html;
      })
      .catch(() => {
        container.innerHTML = `<p class="instagram-placeholder">Instagram feed unavailable.</p>`;
      });
  }

  /* ============================================================
     ANNOUNCEMENT BAR — DISMISSIBLE
     ============================================================ */

  function initAnnouncementBar() {
    const bar      = document.getElementById('AnnouncementBar');
    const dismissBtn = bar?.querySelector('[data-dismiss-announcement]');
    if (!bar || !dismissBtn) return;

    const key = bar.dataset.dismissKey;
    if (key && localStorage.getItem('se_announce_' + key) === 'dismissed') {
      bar.style.display = 'none';
    }

    dismissBtn.addEventListener('click', () => {
      bar.style.display = 'none';
      if (key) localStorage.setItem('se_announce_' + key, 'dismissed');
    });
  }

  /* ============================================================
     FOOTER NEWSLETTER
     ============================================================ */

  function initNewsletter() {
    document.querySelectorAll('[data-newsletter-form]').forEach(form => {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const emailInput = form.querySelector('[type="email"]');
        const message    = form.querySelector('[data-newsletter-message]');
        const btn        = form.querySelector('[type="submit"]');
        if (!emailInput) return;

        btn && (btn.disabled = true);
        try {
          const formData = new FormData(form);
          await fetch('/', { method: 'POST', body: formData });
          if (message) { message.textContent = 'Thanks for subscribing!'; message.classList.add('is-visible'); }
          form.reset();
        } catch {
          if (message) { message.textContent = 'Please try again.'; message.classList.add('is-visible'); }
        } finally {
          btn && (btn.disabled = false);
        }
      });
    });
  }

  /* ============================================================
     QUANTITY SELECTOR
     ============================================================ */

  function initQuantitySelectors() {
    document.querySelectorAll('[data-quantity-selector]').forEach(selector => {
      const input   = selector.querySelector('.quantity-input');
      const minusBtn = selector.querySelector('[data-minus]');
      const plusBtn  = selector.querySelector('[data-plus]');
      if (!input) return;

      minusBtn?.addEventListener('click', () => {
        const val = parseInt(input.value) || 1;
        if (val > 1) input.value = val - 1;
        input.dispatchEvent(new Event('change'));
      });

      plusBtn?.addEventListener('click', () => {
        const val = parseInt(input.value) || 1;
        input.value = val + 1;
        input.dispatchEvent(new Event('change'));
      });
    });
  }

  /* ============================================================
     SEARCH
     ============================================================ */

  function initSearch() {
    const openBtns    = document.querySelectorAll('[data-open-search]');
    const searchPanel = document.getElementById('SearchPanel');
    if (!searchPanel) return;

    const closeBtns   = searchPanel.querySelectorAll('[data-close-search]');
    const backdrop    = searchPanel.querySelector('.site-search__backdrop');
    const input       = searchPanel.querySelector('#SearchInput');
    const resultsBox  = searchPanel.querySelector('#SearchResults');
    const mode        = searchPanel.dataset.searchStyle || 'panel';

    function open() {
      searchPanel.classList.add('is-open');
      searchPanel.setAttribute('aria-hidden', 'false');
      setTimeout(() => input?.focus(), 80);
    }

    function close() {
      searchPanel.classList.remove('is-open');
      searchPanel.setAttribute('aria-hidden', 'true');
      if (resultsBox) resultsBox.innerHTML = '';
    }

    openBtns.forEach(btn => btn.addEventListener('click', open));
    closeBtns.forEach(btn => btn.addEventListener('click', close));
    backdrop?.addEventListener('click', close);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && searchPanel.classList.contains('is-open')) close();
    });

    /* Predictive search */
    if (mode === 'predictive' && input && resultsBox) {
      let debounceTimer;
      let currentQuery = '';

      input.addEventListener('input', () => {
        const query = input.value.trim();
        clearTimeout(debounceTimer);

        if (query.length < 2) {
          resultsBox.innerHTML = '';
          currentQuery = '';
          return;
        }

        debounceTimer = setTimeout(() => {
          if (query !== currentQuery) {
            currentQuery = query;
            fetchPredictiveResults(query, resultsBox);
          }
        }, 280);
      });

      /* Clear results when input is fully cleared */
      input.addEventListener('search', () => {
        if (!input.value) {
          resultsBox.innerHTML = '';
          currentQuery = '';
        }
      });
    }
  }

  function fetchPredictiveResults(query, container) {
    const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[fields]=title,image,price&resources[limit]=10`;

    fetch(url)
      .then(r => r.json())
      .then(data => {
        const products = data?.resources?.results?.products || [];
        renderPredictiveResults(products, query, container);
      })
      .catch(() => {
        container.innerHTML = '';
      });
  }

  function renderPredictiveResults(products, query, container) {
    if (!products.length) {
      container.innerHTML = `<p class="site-search__results-empty">No results for &ldquo;${escapeHTML(query)}&rdquo;</p>`;
      return;
    }

    const items = products.map(p => {
      const imgSrc = p.featured_image?.url
        ? p.featured_image.url.replace(/(\.\w+)(\?|$)/, '_48x48_crop_center$1$2')
        : '';
      const imgTag = imgSrc
        ? `<img class="site-search__result-img" src="${imgSrc}" alt="" loading="lazy" width="48" height="48">`
        : `<div class="site-search__result-img"></div>`;

      return `
        <a href="${p.url}" class="site-search__result-item">
          ${imgTag}
          <div class="site-search__result-info">
            <div class="site-search__result-title">${escapeHTML(p.title)}</div>
            ${p.price ? `<div class="site-search__result-price">${p.price}</div>` : ''}
          </div>
        </a>`;
    }).join('');

    const footer = `
      <div class="site-search__results-footer">
        <a href="/search?type=product&q=${encodeURIComponent(query)}">
          See all results for &ldquo;${escapeHTML(query)}&rdquo; &rarr;
        </a>
      </div>`;

    container.innerHTML = items + footer;
  }

  function escapeHTML(str) {
    return String(str).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
  }

  /* ============================================================
     TOAST NOTIFICATIONS
     ============================================================ */

  let toastTimeout;

  function showToast(message, type = 'success') {
    let toast = document.getElementById('Toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'Toast';
      toast.style.cssText = `
        position:fixed; bottom:1.5rem; left:50%; transform:translateX(-50%) translateY(20px);
        padding:0.75rem 1.5rem; border-radius:2px; font-size:0.85rem; font-weight:600;
        z-index:9999; opacity:0; transition:all 0.3s ease; white-space:nowrap;
        max-width:calc(100vw - 2rem); text-align:center;
      `;
      document.body.appendChild(toast);
    }

    clearTimeout(toastTimeout);
    toast.textContent  = message;
    toast.style.background = type === 'error' ? '#ff3040' : '#094965';
    toast.style.color      = '#fff';

    requestAnimationFrame(() => {
      toast.style.opacity   = '1';
      toast.style.transform = 'translateX(-50%) translateY(0)';
    });

    toastTimeout = setTimeout(() => {
      toast.style.opacity   = '0';
      toast.style.transform = 'translateX(-50%) translateY(20px)';
    }, 3000);
  }

  /* ============================================================
     FOCUS TRAP UTILITIES
     ============================================================ */

  let focusTrapEl   = null;
  let previousFocus = null;

  function trapFocus(el) {
    previousFocus = document.activeElement;
    focusTrapEl   = el;

    const focusable = el.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;

    const first = focusable[0];
    const last  = focusable[focusable.length - 1];

    function handleTab(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last)  { e.preventDefault(); first.focus(); }
      }
    }

    el._trapHandler = handleTab;
    document.addEventListener('keydown', handleTab);
  }

  function releaseFocus() {
    if (focusTrapEl?._trapHandler) {
      document.removeEventListener('keydown', focusTrapEl._trapHandler);
    }
    focusTrapEl = null;
    previousFocus?.focus();
    previousFocus = null;
  }

  /* ============================================================
     UTILITIES
     ============================================================ */

  function formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return `£${amount}`;
  }

  /* ============================================================
     CART PAGE (if using page cart)
     ============================================================ */

  function initCartPage() {
    document.querySelectorAll('[data-cart-remove]').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        const key = btn.dataset.cartRemove;
        await fetch('/cart/change.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ id: key, quantity: 0 }),
        });
        window.location.reload();
      });
    });

    document.querySelectorAll('[data-cart-qty-input]').forEach(input => {
      input.addEventListener('change', async () => {
        const key = input.dataset.cartQtyInput;
        const qty = parseInt(input.value);
        await fetch('/cart/change.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ id: key, quantity: qty }),
        });
        window.location.reload();
      });
    });
  }

  /* ============================================================
     INIT
     ============================================================ */

  function init() {
    initNav();
    initCartDrawer();
    initAddToCart();
    initVariantPicker();
    initProductGallery();
    initSizeChart();
    initCollectionFilters();
    initInfiniteScroll();
    initInstagramFeed();
    initAnnouncementBar();
    initNewsletter();
    initQuantitySelectors();
    initSearch();
    initCartPage();
    initQuickAddOnCards(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
